<template>
  <section class="flex align-middle justify-center sm:py-40">
    <div class="flex flex-col">
      <a class="link--primary mb-16" :href="redirectTo">
        <div class="inline-block md:pl-0 mt-4"><BackIcon /></div>
        <p class="inline-block ml-8">Pricing</p>
      </a>
      <Card class="w-full max-w-448 p-24">
        <form novalidate @submit.prevent="submit()">
          <h1 class="text-h1 mb-16">Request an upgrade</h1>
          <fieldset>
            <legend class="pb-16 text-body text-grey-body">
              Check if your company’s benefits include unlimited access to all of Sittercity’s features
            </legend>
            <CorporateClientComboBox :preselectedId="preselectedId" :errorText="errorText" @selectedClient="selectedClient" />
            <text-field
              id="workEmail"
              v-model="workEmail"
              labelName="Work email"
              :helperText="emailFieldHelperText"
              name="workEmail"
              type="email"
              autocomplete="email"
              class="mb-16"
              :status="workEmailStatus"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Continue" size="medium" type="submit" />
        </form>
      </Card>
      <popup id="errorPopup" v-model="showPopup" popupHeader="We couldn’t send your request" @close="closePopup">
        <p class="text-body mb-16">
          There was an error submitting your request. Try again and if the error continues,
          <a
            href="https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=5652055814427"
            class="text-grey-clickable underline"
            >contact Customer Success</a
          >
        </p>
        <Button
          id="tryAgain"
          variant="primary"
          value="Try again"
          size="medium"
          class="w-full"
          :loading="isLoading"
          @click="closePopup"
        />
      </popup>
    </div>
  </section>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import TextField from '@/components/textfield/TextField.vue';
import Button from '@/components/button/Button.vue';
import Popup from '@/components/popup/Popup.vue';
import BackIcon from '@/icons/BackIcon.vue';
import helperStatus from '../../../../helpers/status';
import { validateEmail } from '../../../../helpers/emailHelper';
import { ADD_EMPLOYER } from '@/graphql/bh_benefits/mutations';
import { sendSegmentEvent } from '@/lib/segment';
import CorporateClientComboBox from '../../components/CorporateClientComboBox.vue';

export default {
  name: 'RequestUpgrade',
  components: {
    Card,
    BackIcon,
    TextField,
    Button,
    Popup,
    CorporateClientComboBox,
  },
  props: {
    redirectTo: { type: String, default: '' },
    preselectedCompany: { type: Object, default: () => {} },
    corporateClients: {
      type: Array,
      default: () => [],
    },
  },
  data(): any {
    return {
      showPopup: false,
      employerNameInput: '',
      selectedCompanySlug: '',

      workEmail: '',
      workEmailStatus: helperStatus.Neutral,
      isLoading: false,
    };
  },
  computed: {
    preselectedId(): string {
      return this.preselectedCompany?.bhClientId || '';
    },
    errorText(): string {
      return 'Enter a valid name';
    },
    emailFieldHelperText() {
      if (this.workEmailStatus === helperStatus.Error) {
        if (this.workEmail.length) {
          return 'Enter a valid email';
        }
        return 'Work email is required';
      }
      return 'Enter the email you use at work';
    },
  },
  watch: {
    workEmail(value) {
      if (this.workEmailStatus === helperStatus.Error) {
        if (validateEmail(value)) {
          this.workEmailStatus = helperStatus.Neutral;
        }
      }
    },
  },
  methods: {
    selectedClient({ slug, name }) {
      this.employerNameInput = name;
      this.selectedCompanySlug = slug;
    },
    submit() {
      this.employerStatus = helperStatus.Neutral;
      const emailValid = validateEmail(this.workEmail);
      const employerValid = this.selectedCompanySlug.length > 0;

      if (!emailValid) {
        this.workEmailStatus = helperStatus.Error;
      }
      if (emailValid && employerValid) {
        this.addEmployerMutation();
        sendSegmentEvent('CorporateBenefit_RequestUpgrade', {
          employerSlugEntered: this.selectedCompanySlug,
          employerNameInput: this.employerNameInput,
        });
      }
    },
    async addEmployerMutation() {
      this.isLoading = true;
      await this.$nextTick();
      try {
        await this.$apollo.mutate({
          mutation: ADD_EMPLOYER,
          variables: {
            workEmail: this.workEmail,
            employerSlug: this.selectedCompanySlug,
            employerName: this.employerNameInput,
            source: 'premium_upgrade',
          },
        });
        this.$emit('advanceStep');
      } catch (error) {
        this.showPopup = true;
      } finally {
        this.isLoading = false;
      }
    },
    closePopup() {
      this.showPopup = false;
    },
  },
};
</script>
